import React, { useContext, useEffect, useState } from "react";
import moment from "moment-timezone";

import IProcessed_Report from "@models/IProcessed_Report";
import IFastBoundReconciliationReportResult, {
    IReconciliationItem,
    ReconciliationCategory,
} from "@models/DTO/IFastBoundReconciliationReportResult";
import IOwTableCols from "@models/IOwTableCols";
import IDownloadReportExcelDTO from "@models/DTO/IDownloadReportExcelDTO";
import IOnDownloadReportExcelDTO from "@models/DTO/IOnDownloadReportExcelDTO";
import EndpointsEnum from "@models/EndpointsEnum";

import OwTitle from "@components/shared/OwTitle/OwTitle";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwTable from "@components/shared/OwTable/OwTable";
import OwButton from "@components/shared/OwButton/OwButton";
import { ControlBar } from "@components/shared/ControlBar/ControlBar";

import UIService from "@services/UIService";
import UserContext from "@contexts/UserContext";
import SupplierContext from "@contexts/SupplierContext";
import { SocketContext } from "@contexts/SocketContext";

type Props = {
    processedReport: IProcessed_Report;
};

type CategoryFilter = ReconciliationCategory | "all" | "matched_unassigned";

const CATEGORY_LABELS: Record<ReconciliationCategory, string> = {
    matched: "Matched",
    fastbound_only: "FastBound Only",
    orderwerks_only: "OW Only",
    status_mismatch: "Status Mismatch",
};

const CATEGORY_BADGE_CLASS: Record<ReconciliationCategory, string> = {
    matched: "badge bg-success",
    fastbound_only: "badge bg-warning text-dark",
    orderwerks_only: "badge bg-info text-dark",
    status_mismatch: "badge bg-danger",
};

export const FastBoundReconciliationReport: React.FC<Props> = ({ processedReport }) => {
    const socket = useContext(SocketContext)!;
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const reportData: IFastBoundReconciliationReportResult = processedReport.report_data;
    const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>("all");
    const [downloadingExcel, setDownloadingExcel] = useState(false);

    const filteredItems =
        categoryFilter === "all"
            ? reportData.items
            : categoryFilter === "matched_unassigned"
              ? reportData.items.filter((i) => i.category === "matched" && i.ow_assigned === false)
              : reportData.items.filter((i) => i.category === categoryFilter);

    const downloadFromBase64 = (base64: string, filename: string) => {
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const onDownloadExcel = (data: IOnDownloadReportExcelDTO) => {
        setDownloadingExcel(false);
        if (data.downloadDirect && data.buffer) {
            downloadFromBase64(data.buffer, data.filename);
        } else if (data.s3Url) {
            window.open(data.s3Url, "_blank");
        }
    };

    const handleDownloadExcel = () => {
        setDownloadingExcel(true);
        const payload: IDownloadReportExcelDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            reportGuid: processedReport.guid,
        };
        socket.emit(EndpointsEnum.ReportDownloadExcel, payload);
    };

    useEffect(() => {
        socket.on(EndpointsEnum.ReportOnDownloadExcel, onDownloadExcel);
        return () => {
            socket.removeListener(EndpointsEnum.ReportOnDownloadExcel, onDownloadExcel);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const renderCategoryBadge = (row: IReconciliationItem) => {
        return <span className={CATEGORY_BADGE_CLASS[row.category]}>{CATEGORY_LABELS[row.category]}</span>;
    };

    const getTableCols = (): IOwTableCols[] => [
        { title: "serial", valueProp: "serial" },
        {
            title: "category",
            valueProp: "category",
            renderFn: renderCategoryBadge,
            renderFnEmitsRow: true,
        },
        { title: "fb_manufacturer", valueProp: "fb_manufacturer" },
        { title: "fb_model", valueProp: "fb_model" },
        { title: "fb_type", valueProp: "fb_type" },
        { title: "fb_caliber", valueProp: "fb_caliber" },
        { title: "fb_status", valueProp: "fb_status" },
        { title: "ow_sku", valueProp: "ow_sku" },
        { title: "ow_name", valueProp: "ow_name" },
        {
            title: "assigned",
            valueProp: "ow_assigned",
            renderFn: (row: IReconciliationItem) =>
                row.ow_assigned == null ? null : row.ow_assigned ? (
                    <span className="badge bg-success">Yes</span>
                ) : (
                    <span className="badge bg-warning text-dark">No</span>
                ),
            renderFnEmitsRow: true,
        },
        { title: "ow_status", valueProp: "ow_fastbound_status" },
        { title: "mismatch_detail", valueProp: "mismatch_detail" },
    ];

    const isDark = !UIService.isMainViewLight();
    const cardClass = "card p-3 text-center " + (isDark ? "bg-dark text-light" : "");

    const filterButtons: { key: CategoryFilter; label: string }[] = [
        { key: "all", label: "All" },
        { key: "matched", label: "Matched" },
        { key: "fastbound_only", label: "FastBound Only" },
        { key: "orderwerks_only", label: "OW Only" },
        { key: "status_mismatch", label: "Status Mismatch" },
        ...(reportData.summary.matched_unassigned > 0
            ? [{ key: "matched_unassigned" as CategoryFilter, label: "Matched Unassigned" }]
            : []),
    ];

    return (
        <div className="FastBoundReconciliationReport">
            <OwTitle title={"fastbound_reconciliation_report"} tag={"h3"} />

            <div className="row mb-3">
                <div className="col-md-8">
                    <ControlBar
                        name={"FastBound Reconciliation Report"}
                        sections={[
                            {
                                name: "Download",
                                elements: [
                                    <OwButton
                                        key="download-btn"
                                        title={downloadingExcel ? "generating_excel" : "excel"}
                                        leftIconClass={downloadingExcel ? "fas fa-spinner fa-spin" : "fas fa-download"}
                                        fn={handleDownloadExcel}
                                        disabled={downloadingExcel}
                                    />,
                                ],
                            },
                        ]}
                    />
                </div>
            </div>

            {/* Account info */}
            <div className={"mb-4 " + (isDark ? "text-light" : "")}>
                {reportData.fastbound_account_name && (
                    <span className="me-4">
                        <OwLabel title={"account"} isBold={true} className="me-1" />
                        {reportData.fastbound_account_name}
                    </span>
                )}
                {reportData.fastbound_account_number && (
                    <span className="me-4">
                        <OwLabel title={"account_number"} isBold={true} className="me-1" />
                        {reportData.fastbound_account_number}
                    </span>
                )}
                {reportData.generated_at && (
                    <span>
                        <OwLabel title={"generated_at"} isBold={true} className="me-1" />
                        {moment(reportData.generated_at).format("YYYY-MM-DD HH:mm")}
                    </span>
                )}
            </div>

            {/* Summary cards */}
            <div className="row mb-4 g-3">
                <div className="col-6 col-md">
                    <div className={cardClass}>
                        <div className="fs-4 fw-bold">{reportData.summary.total_fastbound_items}</div>
                        <div className="small">FastBound Items</div>
                    </div>
                </div>
                <div className="col-6 col-md">
                    <div className={cardClass}>
                        <div className="fs-4 fw-bold">{reportData.summary.total_orderwerks_serials}</div>
                        <div className="small">OW Serials</div>
                    </div>
                </div>
                <div className="col-6 col-md">
                    <div className={cardClass}>
                        <div className="fs-4 fw-bold text-success">{reportData.summary.matched}</div>
                        <div className="small">Matched</div>
                    </div>
                </div>
                <div className="col-6 col-md">
                    <div className={cardClass}>
                        <div className="fs-4 fw-bold text-warning">{reportData.summary.matched_unassigned}</div>
                        <div className="small">Matched Unassigned</div>
                    </div>
                </div>
                <div className="col-6 col-md">
                    <div className={cardClass}>
                        <div className="fs-4 fw-bold text-warning">{reportData.summary.fastbound_only}</div>
                        <div className="small">FastBound Only</div>
                    </div>
                </div>
                <div className="col-6 col-md">
                    <div className={cardClass}>
                        <div className="fs-4 fw-bold text-info">{reportData.summary.orderwerks_only}</div>
                        <div className="small">OW Only</div>
                    </div>
                </div>
                <div className="col-6 col-md">
                    <div className={cardClass}>
                        <div className="fs-4 fw-bold text-danger">{reportData.summary.status_mismatch}</div>
                        <div className="small">Status Mismatch</div>
                    </div>
                </div>
            </div>

            {/* Category filter */}
            <div className="mb-3 d-flex gap-2 flex-wrap">
                {filterButtons.map(({ key, label }) => (
                    <button
                        key={key}
                        className={"btn btn-sm " + (categoryFilter === key ? "btn-primary" : "btn-outline-secondary")}
                        onClick={() => setCategoryFilter(key)}>
                        {label}
                        {key !== "all" && (
                            <span className="ms-1 badge bg-secondary">
                                {key === "matched_unassigned"
                                    ? reportData.items.filter((i) => i.category === "matched" && i.ow_assigned === false).length
                                    : reportData.items.filter((i) => i.category === key).length}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {/* Items table */}
            <OwTable
                columns={getTableCols()}
                dataSet={filteredItems}
                dataFetch={false}
                initialData={false}
                userModel={userModel}
                supplierModel={supplierModel}
            />
        </div>
    );
};
